import { useEffect, useMemo, useRef, useState } from 'react';
import { useOutletContext, useSearchParams } from 'react-router-dom';
import { Button } from '../../../../components/Button';
import { ConfirmModal } from '../../../../components/ConfirmModal';
import {
  removeScholarshipPlayer,
  setScholarshipPlayerRecruitingStatus,
} from '../../../../services/scholarships';
import {
  type RecruitingStatus,
  type ScholarshipClass,
  type ScholarshipPlayer,
} from '../../../../types/scholarships';
import { downloadBlobFile } from '../../../../utils/file-download';
import { getSafeErrorMessage } from '../../../../utils/errorHandler';
import { buildRosterCsv, rosterCsvFileName } from './rosterCsv';
import type { ScholarshipDetailContext } from './ScholarshipDetailLayout';
import {
  isPositionFilter,
  type PositionFilter,
  type PositionGroup,
} from './positionGroups';
import { useRosterSport } from './useRosterSport';
import { useRosterPlayers } from './useRosterPlayers';
import { diamondSortComparator } from './depthChartPositions';
import { rosterSettingsOf } from './rosterPalette';
import { isScholarshipClass } from './scholarshipClasses';
import { matchesRosterFilters } from './rosterFilters';
import { ProspectQuickViewPanel } from '../players/ProspectQuickViewPanel';
import { AttachProspectsModal } from './AttachProspectsModal';
import { ScholarshipPlayerModal } from './ScholarshipPlayerModal';
import { ScholarshipFiltersModal } from './ScholarshipFiltersModal';
import { ScholarshipPlayersTable } from './ScholarshipPlayersTable';
import { TransferPlayerModal } from './TransferPlayerModal';
import { ScholarshipTotalsStrip } from './ScholarshipTotalsStrip';
import { tourAnchor } from '../../../tours/tourAnchors';
import { useTour } from '../../../tours/useTour';
import {
  FILTER_PARAM_KEYS,
  loadScholarshipFilters,
  saveScholarshipFilters,
} from './scholarshipFilterStorage';
import { isRecruitingBoard } from './boardPurpose';
import './scholarshipPlayers.css';

type ModalState =
  | { mode: 'add' }
  | { mode: 'edit'; player: ScholarshipPlayer };

const parsePositions = (raw: string | null): PositionFilter[] => {
  if (!raw) return [];
  return raw.split(',').filter(isPositionFilter);
};

const parseClasses = (raw: string | null): ScholarshipClass[] => {
  if (!raw) return [];
  return raw.split(',').filter(isScholarshipClass);
};

export const ScholarshipPlayersTab = () => {
  // DH renders as DP for a softball program; see positionLabel.
  const sport = useRosterSport();
  const { scholarship, refresh: refreshScholarship } = useOutletContext<ScholarshipDetailContext>();
  const [searchParams, setSearchParams] = useSearchParams();
  // Load, the optimistic reorder with its 409 refetch, and the merge after a save, shared with the
  // depth chart and the pitching staff. A saved order moves the summary card's figures too.
  const {
    players,
    error,
    setError,
    refresh: refreshPlayers,
    setPlayers,
    reorder: handleReorder,
    mergeSaved,
    listVersion,
    replaceAll,
  } = useRosterPlayers(scholarship.id, refreshScholarship);
  const [modalState, setModalState] = useState<ModalState | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ScholarshipPlayer | null>(null);
  const [attachOpen, setAttachOpen] = useState(false);
  // The player record behind an attached row, opened in the same viewer the Players page uses.
  const [viewProspectId, setViewProspectId] = useState<string | null>(null);
  const [collapsedGroups, setCollapsedGroups] = useState<Set<PositionGroup>>(new Set());
  /*
    The players a coach has just marked committed, each offered a move onto a roster management
    board. Held separately from the status write: the commitment is already saved by the time a
    player reaches this, so dismissing the offer changes nothing.

    A QUEUE, not one slot. Each tick is a round trip, so ticking three boxes in quick succession
    lands three responses -- the second and third after the first player's dialog is already up.
    Overwriting the player on the open dialog left the roster the coach had picked, the in-flight
    flag and the dismissal flag all behind from the player before, and pressing Move player then
    moved the WRONG player, because the handler reads the id off the current prop. Each offer now
    waits its turn and gets its own dialog.
  */
  const [transferQueue, setTransferQueue] = useState<ScholarshipPlayer[]>([]);
  const transferTarget = transferQueue[0] ?? null;
  const dropFromTransferQueue = (playerId: string) =>
    setTransferQueue((queue) => queue.filter((p) => p.id !== playerId));

  // Filters live in an expand-down panel behind a top-bar "Filters" button.
  // Default minimized (collapsed) so the table gets full width; the choice
  // persists per-scholarship, and the toggle carries an active-filter badge.
  // Not persisted any more: a modal is opened for a moment and dismissed, unlike the
  // expand-down panel it replaces, whose open state had to survive a reload.
  const [filtersOpen, setFiltersOpen] = useState(false);

  // Cost pre-fill — same component lifetime, resets on unmount.
  const lastCostRef = useRef<number | null>(null);

  const positionFilters = useMemo(
    () => parsePositions(searchParams.get('positions')),
    [searchParams],
  );
  const classFilters = useMemo(
    () => parseClasses(searchParams.get('classes')),
    [searchParams],
  );

  // Restore saved filters on mount when the URL has none. Deep-linking with
  // explicit params wins; this only fills in when the user came back to the
  // tab via nav and the URL is bare.
  useEffect(() => {
    const hasUrlFilters = FILTER_PARAM_KEYS.some((k) => searchParams.has(k));
    if (hasUrlFilters) return;
    const saved = loadScholarshipFilters('players', scholarship.id);
    if (!saved) return;
    const next = new URLSearchParams(searchParams);
    for (const k of FILTER_PARAM_KEYS) {
      const v = saved.get(k);
      if (v) next.set(k, v);
    }
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scholarship.id]);

  // Persist filters on every change so they survive navigation away to the
  // depth chart sub-tab and back.
  useEffect(() => {
    saveScholarshipFilters('players', scholarship.id, searchParams);
  }, [scholarship.id, searchParams]);

  const updateFilterParam = (key: 'positions' | 'classes', values: string[]) => {
    const next = new URLSearchParams(searchParams);
    if (values.length === 0) next.delete(key);
    else next.set(key, values.join(','));
    setSearchParams(next, { replace: false });
  };

  const togglePosition = (filter: PositionFilter) => {
    const next = positionFilters.includes(filter)
      ? positionFilters.filter((f) => f !== filter)
      : [...positionFilters, filter];
    updateFilterParam('positions', next);
  };

  const toggleClass = (playerClass: ScholarshipClass) => {
    const next = classFilters.includes(playerClass)
      ? classFilters.filter((c) => c !== playerClass)
      : [...classFilters, playerClass];
    updateFilterParam('classes', next);
  };

  const clearFilters = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('positions');
    next.delete('classes');
    setSearchParams(next, { replace: false });
  };

  const toggleCollapseGroup = (group: PositionGroup) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(group)) next.delete(group);
      else next.add(group);
      return next;
    });
  };

  const filteredPlayers = useMemo(() => {
    if (players == null) return [];
    return players.filter((p) => matchesRosterFilters(p, positionFilters, classFilters));
  }, [players, positionFilters, classFilters]);

  /*
    A save already reads the board back, and closing the modal unmounts the teams control, whose
    one-shot "something changed" notice reads it back again -- four board reads for a save that
    also touched a team, where two say everything. The save's own refresh is the later of the two,
    so it is the one that stands and the notice arriving behind it is spent.

    Cleared when the modal opens, so a save with no team change cannot leave the flag set for a
    later edit that IS closed with Cancel and does need its refresh.
  */
  const savedRefreshRef = useRef(false);

  const openPlayerModal = (next: ModalState) => {
    savedRefreshRef.current = false;
    setModalState(next);
  };

  const handleModalSuccess = (submittedCost: number | null, saved: ScholarshipPlayer | null) => {
    if (modalState?.mode === 'add' && submittedCost != null) {
      lastCostRef.current = submittedCost;
    }
    savedRefreshRef.current = true;
    setModalState(null);
    mergeSaved(saved);
    refreshPlayers();
    refreshScholarship();
  };

  const handleProspectChanged = () => {
    if (savedRefreshRef.current) {
      savedRefreshRef.current = false;
      return;
    }
    // A team change lands on the player record, not on this row, so the row's team list (and
    // anything else the API resolves from that record) is refetched.
    refreshPlayers();
    refreshScholarship();
  };

  /*
    The board's purpose decides this, and nothing else.

    Deriving it from the rows as well — any attached row, or any row with a status set — meant
    that attaching players to a Roster Management board grew two recruiting columns nobody had
    asked for. Reported from a brand-new organization, where the first thing a coach does is
    attach their existing players, so the columns showed up on day one on a board that is about
    money. A coach who wants to track offers has the Recruiting Board kind for exactly that.
  */
  const showRecruitingStatus = scholarship.purpose === 'Recruiting';

  const handleRecruitingStatusChange = async (
    player: ScholarshipPlayer,
    next: RecruitingStatus,
  ) => {
    if (!players) return;
    const snapshot = players;

    // Optimistic — a tick that waits on a round trip feels broken when a coach is working
    // down a list of twenty.
    setPlayers(
      players.map((p) =>
        p.id === player.id ? { ...p, recruitingStatus: next } : p,
      ),
    );
    setError(null);

    try {
      const updated = await setScholarshipPlayerRecruitingStatus(
        scholarship.id,
        player.id,
        next,
      );
      /*
        A linked row's status lives on the player record, so every row for that same player on
        this board moves together — otherwise two rows for one person would disagree until
        reload. A manual row owns its own status, so only that row moves.
      */
      setPlayers((current) =>
        (current ?? []).map((p) => {
          if (updated.prospectId != null) {
            return p.prospectId === updated.prospectId ? updated : p;
          }
          return p.id === updated.id ? updated : p;
        }),
      );
      /*
        Client ask: once a player commits, offer to move them onto a roster. Offered rather than
        done, and only after the status write has landed, so the commitment is saved whatever the
        coach decides about the move. Only on the way INTO Committed -- re-ticking a box that is
        already ticked is not a fresh commitment.
      */
      if (
        showRecruitingStatus &&
        next === 'Committed' &&
        (player.recruitingStatus ?? 'None') !== 'Committed'
      ) {
        setTransferQueue((queue) =>
          queue.some((p) => p.id === updated.id) ? queue : [...queue, updated],
        );
      }
    } catch (e) {
      setPlayers(snapshot);
      setError(getSafeErrorMessage(e, 'Failed to update recruiting status'));
    }
  };

  // Exports what the coach is looking at — the filtered rows and the columns the board is
  // showing — rather than the whole roster with every field. A file that disagreed with the
  // screen it came from would be worse than no export.
  const handleExport = () => {
    const isPercentage = scholarship.scholarshipModel === 'Percentage';
    const csv = buildRosterCsv(filteredPlayers, {
      showUnits: scholarship.scholarshipModel === 'FullHalf',
      showNil: scholarship.nilTrackingEnabled,
      showRevShare: scholarship.revShareTrackingEnabled,
      showPackage: scholarship.nilTrackingEnabled || scholarship.revShareTrackingEnabled,
      showScholarshipPercent: isPercentage,
      showRecruitingStatus,
    }, sport);
    downloadBlobFile(
      new Blob([csv], { type: 'text/csv;charset=utf-8;' }),
      rosterCsvFileName(scholarship.name),
    );
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await removeScholarshipPlayer(scholarship.id, deleteTarget.id);
      setDeleteTarget(null);
      refreshPlayers();
      refreshScholarship();
    } catch (e) {
      setError(getSafeErrorMessage(e, 'Failed to remove player'));
    }
  };

  // Registered on this tab rather than in the layout: the attach, add and export buttons and every
  // row anchor live here. A recruiting board runs its own tours: they say Configure board and
  // Pitchers, and skip the lineups a recruiting board does not have.
  useTour(isRecruitingBoard(scholarship) ? 'recruiting-board' : 'rosters', {
    ready: players != null || error != null,
    hasData: filteredPlayers.length > 0,
  });

  if (players == null && !error) {
    return <div>Loading players...</div>;
  }

  const noPlayers = players != null && players.length === 0;
  const activeFilterCount = positionFilters.length + classFilters.length;

  return (
    <div className="scholarship-players">
      <div className="scholarship-players__top-bar">
        {!noPlayers && (
          <Button
            variant="secondary"
            onClick={() => setFiltersOpen(true)}
            data-testid="players-filter-toggle"
            aria-label={
              activeFilterCount > 0 ? `Filters (${activeFilterCount} active)` : 'Filters'
            }
          >
            Filters
            {activeFilterCount > 0 && (
              <span className="scholarship-players__filter-toggle-badge" aria-hidden="true">
                {activeFilterCount}
              </span>
            )}
          </Button>
        )}
        {!noPlayers && (
          <Button
            variant="secondary"
            onClick={handleExport}
            {...tourAnchor('rosters.export')}
          >
            Export CSV
          </Button>
        )}
        <Button
          variant="secondary"
          onClick={() => setAttachOpen(true)}
          {...tourAnchor('rosters.attach')}
        >
          + Attach players
        </Button>
        <Button
          variant="primary"
          onClick={() => openPlayerModal({ mode: 'add' })}
          {...tourAnchor('rosters.add')}
        >
          + Add player
        </Button>
      </div>

      {error && (
        <div className="scholarship-players__error" role="alert">{error}</div>
      )}

      {noPlayers ? (
        <div className="scholarship-players__empty-state">
          <p>No players on this roster yet.</p>
          {/*
            Attach leads on an empty roster: pulling from the pool the org already maintains
            is nearly always the faster route than typing a player in from scratch.
          */}
          <Button variant="primary" onClick={() => setAttachOpen(true)}>
            + Attach players
          </Button>
          <Button variant="secondary" onClick={() => openPlayerModal({ mode: 'add' })}>
            + Add a player manually
          </Button>
        </div>
      ) : (
        <>
          <ScholarshipPlayersTable
            sport={sport}
            settings={rosterSettingsOf(scholarship)}
            players={filteredPlayers}
            scholarshipModel={scholarship.scholarshipModel}
            showNil={scholarship.nilTrackingEnabled}
            showRevShare={scholarship.revShareTrackingEnabled}
            // Recruiting boards only — see showRecruitingStatus above.
            showRecruitingStatus={showRecruitingStatus}
            collapsedGroups={collapsedGroups}
            onToggleGroup={toggleCollapseGroup}
            onEdit={(p) => openPlayerModal({ mode: 'edit', player: p })}
            onRemove={(p) => setDeleteTarget(p)}
            onOpenProspect={setViewProspectId}
            onRecruitingStatusChange={handleRecruitingStatusChange}
            onReorder={handleReorder}
          />
          <ScholarshipTotalsStrip
            players={filteredPlayers}
            showNil={scholarship.nilTrackingEnabled}
            showRevShare={scholarship.revShareTrackingEnabled}
          />
        </>
      )}

      {filtersOpen && (
        <ScholarshipFiltersModal
          sport={sport}
          players={players ?? []}
          positionFilters={positionFilters}
          classFilters={classFilters}
          onTogglePosition={togglePosition}
          onToggleClass={toggleClass}
          onClear={clearFilters}
          onClose={() => setFiltersOpen(false)}
        />
      )}

      {attachOpen && (
        <AttachProspectsModal
          scholarshipId={scholarship.id}
          attachedProspectIds={(players ?? [])
            .map((p) => p.prospectId)
            .filter((id): id is string => id != null)}
          onClose={() => setAttachOpen(false)}
          onAttached={(next) => {
            // The endpoint returns the whole roster, so this replaces rather than appends —
            // sort order is only meaningful across a full position pool.
            setPlayers([...next].sort(diamondSortComparator));
            setAttachOpen(false);
          }}
        />
      )}

      {modalState && (
        <ScholarshipPlayerModal
          recruitingBoard={isRecruitingBoard(scholarship)}
          sport={sport}
          rosterSettings={rosterSettingsOf(scholarship)}
          scholarshipId={scholarship.id}
          scholarshipModel={scholarship.scholarshipModel}
          mode={modalState.mode}
          existingPlayer={modalState.mode === 'edit' ? modalState.player : undefined}
          defaultCost={modalState.mode === 'add' ? lastCostRef.current : undefined}
          onClose={() => setModalState(null)}
          onSuccess={handleModalSuccess}
          onProspectChanged={handleProspectChanged}
        />
      )}

      <ConfirmModal
        isOpen={deleteTarget != null}
        title="Remove player?"
        message={
          deleteTarget
            ? `Remove ${deleteTarget.firstName} ${deleteTarget.lastName} from this scholarship?`
            : ''
        }
        confirmText="Remove"
        onConfirm={handleConfirmDelete}
        onClose={() => setDeleteTarget(null)}
      />

      {transferTarget && (
        <TransferPlayerModal
          // Keyed on the player, so nothing from the offer before it -- the roster picked, the
          // in-flight flag -- can survive into this one.
          key={transferTarget.id}
          scholarshipId={scholarship.id}
          player={transferTarget}
          onClose={() => dropFromTransferQueue(transferTarget.id)}
          listVersion={listVersion}
          onTransferred={(remaining, since) => {
            /*
              Reached even when the coach pressed "Not now" while the move was in flight: the
              dialog is gone by then, but the server has moved the player and this board owns the
              list. Ignoring it left a row here for a player who is no longer on this board, with
              the counts wrong and the next edit of that row 404ing.
            */
            const movedId = transferTarget.id;
            dropFromTransferQueue(movedId);
            /*
              The response is the source board's remaining players, so it replaces the list rather
              than filtering one row out of it -- but only while it still describes the list on
              screen. A dismissed offer outlives its dialog, and the page is live behind it: attach
              two players while the move is out and this reply would put the pre-attach roster
              back, silently, with the totals already counting the attached two.

              Out of date, the one thing it is still authoritative about is applied -- that player
              has left this board -- and the board is read again for the rest.
            */
            if (!replaceAll(remaining, since)) {
              setPlayers((current) => current?.filter((p) => p.id !== movedId) ?? current);
              refreshPlayers();
            }
            refreshScholarship();
          }}
        />
      )}

      {/*
        The same viewer the Players page opens, so a coach reads a recruit's metrics, videos and
        notes without leaving the board they are building. Editing a player in here can change
        what the roster row shows — name, position, grad year — and deleting one leaves an
        orphaned row, so a save refetches the board.
      */}
      <ProspectQuickViewPanel
        prospectId={viewProspectId}
        isOpen={viewProspectId !== null}
        onClose={() => setViewProspectId(null)}
        onProspectChanged={() => {
          refreshPlayers();
          refreshScholarship();
        }}
      />
    </div>
  );
};
